/**
 * LowBatteryWidget
 * Visar fordon med låg batterispänning
 * Adresser laddas lazily via reverse geocoding efter att listan visats
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from '@/store/languageStore';
import { Battery0Icon } from '@heroicons/react/24/outline';
import { BaseDashboardWidget } from './BaseDashboardWidget';
import { reverseGeocode } from '@/services/geocoding/geocodingApi';

interface LowBatteryVehicle {
  vehicleId: number;
  registrationNumber: string;
  make?: string;
  model?: string;
  organizationName?: string;
  voltage: number;
  lastStatusUpdate?: string;
  latitude?: number;
  longitude?: number;
}

const getVoltageColor = (voltage: number) => {
  if (voltage < 11) return 'text-red-500';
  if (voltage < 11.5) return 'text-orange-500';
  return 'text-yellow-500';
};

const LowBatteryItem: React.FC<{ vehicle: LowBatteryVehicle }> = React.memo(({ vehicle }) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current || vehicle.latitude == null || vehicle.longitude == null) return;
    fetched.current = true;
    setLoading(true);
    reverseGeocode(vehicle.latitude, vehicle.longitude)
      .then(result => setAddress(result?.address ?? null))
      .catch(() => setAddress(null))
      .finally(() => setLoading(false));
  }, [vehicle.latitude, vehicle.longitude]);

  const displayAddress = loading
    ? '...'
    : address ?? (vehicle.latitude != null ? t('dashboard.addressUnavailable') : undefined);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-textDefault">{vehicle.registrationNumber}</span>
          {vehicle.make && (
            <span className="text-xs text-textTertiary">{vehicle.make}</span>
          )}
          <span className={`text-xs font-medium ${getVoltageColor(vehicle.voltage)}`}>
            {vehicle.voltage.toFixed(1)}V
          </span>
        </div>
        {vehicle.lastStatusUpdate && (
          <span className="text-xs text-textTertiary shrink-0">
            {new Date(vehicle.lastStatusUpdate).toLocaleString('sv-SE', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>
      {displayAddress && (
        <div className="text-xs text-textTertiary truncate" title={address || undefined}>
          {displayAddress}
        </div>
      )}
    </div>
  );
});

const LowBatteryWidget: React.FC = () => {
  const { t } = useTranslation();

  const vehicleAssignments = useMemo(
    () => ({
      categoryCode: 'LOW_BATTERY',
      getVehicleId: (v: LowBatteryVehicle) => v.vehicleId,
      getRegistrationNumber: (v: LowBatteryVehicle) => v.registrationNumber,
      buildAssignmentTitle: (v: LowBatteryVehicle) =>
        `${t('widgets.lowBattery')}: ${v.registrationNumber}`,
    }),
    [t],
  );

  return (
    <BaseDashboardWidget<LowBatteryVehicle>
      icon={Battery0Icon}
      colorScheme="yellow"
      title={t('widgets.lowBattery')}
      subtitle={t('widgets.lowBattery')}
      apiEndpoint="/api/dashboard/data/low-battery-vehicles"
      apiParams={{ threshold: 12 }}
      warningBadge="<12V"
      emptyMessage={t('widgets.allBatteriesOk')}
      getItemKey={(vehicle) => vehicle.vehicleId}
      vehicleAssignments={vehicleAssignments}
      renderItem={(vehicle) => <LowBatteryItem vehicle={vehicle} />}
    />
  );
};

export default LowBatteryWidget;
